'use client';

import { c as _c } from "react/compiler-runtime";
import { getShopifyScriptTags, initializeShopifyScripts, type ShopifyScriptsI18n } from '@shopify/hydrogen';
import { useRouter } from 'next/navigation';
import { createElement, useEffect } from 'react';
const SHOP_ID = process.env.NEXT_PUBLIC_SHOPIFY_SHOP_ID;
const STOREFRONT_ID = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ID;
const STORE_DOMAIN = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN;
const SHOPIFY_ANALYTICS_ENABLED = process.env.NEXT_PUBLIC_SHOPIFY_ANALYTICS_ENABLED !== 'false';
const STOREFRONT_ACCESS_TOKEN = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN;
// Shopify's hosted privacy banner by default; with the banner off, only the bare
// consent-tracking API loads (Hydrogen's `custom-banner` mode).
const CONSENT_MODE = process.env.NEXT_PUBLIC_SHOPIFY_WITH_PRIVACY_BANNER === 'false' ? 'custom-banner' : 'default-banner';
interface ShopifyScriptsProps {
  countryCode: string;
  languageCode: string;
  currencyCode: string;
}

/**
 * Renders Shopify's storefront scripts (analytics bus, standard events, Shopify
 * analytics destination, consent/privacy banner, perf kit) into the
 * server-rendered shell, then wires up routing and page-view standard events
 * after hydration. Mirrors Hydrogen's `<ShopifyScripts>` React binding, which
 * can't be used directly because it needs the market props at render time.
 */
export function ShopifyScripts(t0) {
  const $ = _c(8);
  const {
    countryCode,
    languageCode,
    currencyCode
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] !== router) {
    t1 = () => {
      initializeShopifyScripts({
        navigate: url => router.push(url),
        webMcp: false
      });
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!SHOP_ID || !STOREFRONT_ID || !STORE_DOMAIN) {
    return null;
  }
  let t3;
  if ($[3] !== countryCode || $[4] !== currencyCode || $[5] !== languageCode) {
    const {
      tags
    } = getShopifyScriptTags({
      analytics: {
        channel: "hydrogen"
      },
      i18n: {
        country: countryCode.toUpperCase(),
        language: languageCode.toUpperCase(),
        currency: currencyCode
      } as ShopifyScriptsI18n,
      shop: {
        shopId: SHOP_ID,
        storefrontId: STOREFRONT_ID,
        myshopifyDomain: STORE_DOMAIN
      },
      consent: {
        mode: CONSENT_MODE
      },
      shopifyAnalytics: SHOPIFY_ANALYTICS_ENABLED
    });
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = createElement("script", {
        key: "shopify-features",
        id: "shopify-features",
        type: "application/json",
        dangerouslySetInnerHTML: {
          __html: JSON.stringify({
            accessToken: STOREFRONT_ACCESS_TOKEN
          }).replace(/</g, "\\u003c")
        }
      });
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t3 = [t4, ...tags.map(_temp)];
    $[3] = countryCode;
    $[4] = currencyCode;
    $[5] = languageCode;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp(t0, index) {
  const {
    tagName,
    attributes: t1,
    innerHTML
  } = t0;
  const attributes = t1 === undefined ? {} : t1;
  const {
    crossorigin,
    ...rest
  } = attributes as Record<string, unknown>;
  return createElement(tagName, {
    key: index,
    ...rest,
    ...(crossorigin !== undefined ? {
      crossOrigin: crossorigin
    } : {}),
    ...(rest.async === true && typeof rest.src === "string" ? {
      onLoad: noop
    } : {}),
    ...(innerHTML ? {
      dangerouslySetInnerHTML: {
        __html: innerHTML
      }
    } : {})
  });
}
function noop() {}